import { Component, HostListener, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import Swal from 'sweetalert2';
import {
  InvoiceSubscriptionsV2Service,
  InvoiceSubscriptionStatus,
  InvoiceSubscriptionPlan,
  InvoiceSubscriptionHistoryItem,
  InvoiceSubscriptionOrder,
  InvoiceDocExtensionPlan,
  InvoiceCompanyExtensionPlan,
  InvoiceTokenExtensionPlan,
  InvoicePurchaseResult,
  InvoiceAutoRenewStatus,
  InvoiceCancelOrderResponse,
} from 'src/app/services/invoice-subscriptions-v2.service';
import { MerchantPaymentService } from 'src/app/services/merchant-payment.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

type Lang = 'ar' | 'he' | 'en';

@Component({
  selector: 'app-merchant-subscriptions',
  templateUrl: './merchant-subscriptions.component.html',
  styleUrls: ['./merchant-subscriptions.component.scss'],
})
export class MerchantSubscriptionsComponent implements OnInit {
  lang: Lang = 'ar';
  userType = Number(localStorage.getItem('user_type'));
  merchantId = Number(localStorage.getItem('merchant_id'));

  // Status
  status: InvoiceSubscriptionStatus | null = null;
  loadingStatus = false;

  // Plans
  plans: InvoiceSubscriptionPlan[] = [];
  docExtensions: InvoiceDocExtensionPlan[] = [];
  companyExtensions: InvoiceCompanyExtensionPlan[] = [];
  tokenExtensions: InvoiceTokenExtensionPlan[] = [];
  loadingPlans = false;

  // History & orders
  history: InvoiceSubscriptionHistoryItem[] = [];
  orders: InvoiceSubscriptionOrder[] = [];
  loadingHistory = false;
  loadingOrders = false;

  // Merchant transactions (card-registration/merchant/:id/transactions)
  transactions: any[] = [];
  loadingTx = false;
  loadingInvoicePdf = false;
  showInvoicePanel = false;
  invoicePdfUrl: SafeResourceUrl | null = null;
  invoicePdfRawUrl: string | null = null;

  // Auto renew
  autoRenew: InvoiceAutoRenewStatus | null = null;
  loadingAutoRenew = false;

  // UI state
  activeTab: 'overview' | 'plans' | 'history' | 'orders' | 'transactions' = 'overview';
  /** Overview tab: collapsible sections (default expanded) */
  overviewSectionsOpen = { period: true, quotas: true, footer: true };
  selectedPlanType: 'subscription' | 'doc' | 'company' | 'token' = 'subscription';
  selectedPlanId: number | null = null;
  autoRenewOnPurchase = false;
  /** Subscription checkout: remember card (independent of auto-renew; backend forces save when auto_renew is true) */
  saveCardOnSubscriptionPurchase = false;
  /** Save card checkbox — doc / company add-ons only (not token) */
  saveCardOnAddonPurchase = false;
  purchasing = false;
  autoRenewSelectionMode = false;
  cancellingOrderId: number | null = null;

  /** From GET card-registration/merchant/:id/saved-card-status */
  hasActiveSavedCard: boolean | null = null;
  loadingSavedCardStatus = false;

  constructor(
    private svc: InvoiceSubscriptionsV2Service,
    private translate: TranslateService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private router: Router,
    private merchantPayment: MerchantPaymentService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    const storedLang = (localStorage.getItem('curentLang') as Lang | null) || 'ar';
    this.lang = (this.translate.currentLang as Lang) || storedLang;

    // Only merchant (100) or assistant merchant (300 / 301)
    if (![100, 300, 301].includes(this.userType) || !this.merchantId) {
      this.router.navigate(['/dashboard/dashboard']);
      return;
    }

    this.refreshAll();
  }

  goMerchantPayment(): void {
    this.router.navigate(['/dashboard/merchant-payment']);
  }

  refreshAll(): void {
    this.loadStatus();
    this.loadPlans();
    this.loadHistory();
    this.loadTransactions();
    this.loadOrders();
    this.loadAutoRenew();
    this.loadSavedCardStatus();
  }

  loadTransactions(): void {
    if (!this.merchantId) {
      this.transactions = [];
      return;
    }
    this.loadingTx = true;
    this.merchantPayment.getTransactions(this.merchantId).subscribe({
      next: (res) => {
        this.transactions = res || [];
        this.loadingTx = false;
      },
      error: () => {
        this.loadingTx = false;
      },
    });
  }

  private resolveInvoiceId(tx: any): number | null {
    const possibleId =
      tx?.issuedInvoiceId ??
      tx?.invoiceId ??
      tx?.invoice?.id ??
      tx?.issuedInvoice?.id;
    const invoiceId = Number(possibleId);
    return Number.isFinite(invoiceId) && invoiceId > 0 ? invoiceId : null;
  }

  showInvoice(tx: any): void {
    const invoiceId = this.resolveInvoiceId(tx);
    if (!invoiceId) {
      Swal.fire({
        icon: 'warning',
        title: this.lang === 'en' ? 'Invoice is not available' : (this.lang === 'he' ? 'החשבונית אינה זמינה' : 'الفاتورة غير متاحة'),
      });
      return;
    }

    this.loadingInvoicePdf = true;
    this.merchantPayment.getInvoicePdfLink(invoiceId, this.lang).subscribe({
      next: (res) => {
        this.loadingInvoicePdf = false;
        if (!res?.pdf_link) return;
        this.invoicePdfRawUrl = res.pdf_link;
        this.invoicePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(res.pdf_link);
        this.showInvoicePanel = true;
      },
      error: () => {
        this.loadingInvoicePdf = false;
        Swal.fire({
          icon: 'error',
          title: this.lang === 'en' ? 'Could not load invoice PDF' : (this.lang === 'he' ? 'לא ניתן לטעון את קובץ ה־PDF של החשבונית' : 'تعذر تحميل ملف PDF الخاص بالفاتورة'),
        });
      },
    });
  }

  closeInvoicePanel(): void {
    this.showInvoicePanel = false;
    this.invoicePdfUrl = null;
    this.invoicePdfRawUrl = null;
  }

  openInvoiceInNewTab(): void {
    if (!this.invoicePdfRawUrl) return;
    window.open(this.invoicePdfRawUrl, '_blank', 'noopener,noreferrer');
  }

  @HostListener('document:keydown.escape')
  onEscapePressed(): void {
    if (this.showInvoicePanel) {
      this.closeInvoicePanel();
    }
  }

  loadSavedCardStatus(): void {
    if (!this.merchantId) {
      return;
    }
    this.loadingSavedCardStatus = true;
    this.merchantPayment.getSavedCardStatus(this.merchantId).subscribe({
      next: (res) => {
        this.hasActiveSavedCard = !!res?.has_active_saved_card;
        this.loadingSavedCardStatus = false;
        if (this.hasActiveSavedCard && this.selectedPlanId) {
          if (this.selectedPlanType === 'subscription') {
            this.saveCardOnSubscriptionPurchase = false;
          } else if (this.selectedPlanType === 'doc' || this.selectedPlanType === 'company') {
            this.saveCardOnAddonPurchase = false;
          }
        }
      },
      error: () => {
        this.hasActiveSavedCard = false;
        this.loadingSavedCardStatus = false;
      },
    });
  }

  toggleOverviewSection(section: 'period' | 'quotas' | 'footer'): void {
    this.overviewSectionsOpen[section] = !this.overviewSectionsOpen[section];
  }

  // ── Loaders ────────────────────────────────────────────────────────────────

  loadStatus(): void {
    this.loadingStatus = true;
    this.svc.getStatus().subscribe({
      next: (res) => {
        this.status = res;
        this.loadingStatus = false;
      },
      error: () => {
        this.loadingStatus = false;
      },
    });
  }

  loadPlans(): void {
    this.loadingPlans = true;
    this.svc.getPlans().subscribe({
      next: (plans) => {
        this.plans = plans.filter((p) => p.is_active);
        this.loadingPlans = false;
      },
      error: () => {
        this.loadingPlans = false;
      },
    });

    this.svc.getDocExtensions().subscribe({
      next: (ext) => (this.docExtensions = ext || []),
      error: () => (this.docExtensions = []),
    });

    this.svc.getCompanyExtensions().subscribe({
      next: (ext) => (this.companyExtensions = ext || []),
      error: () => (this.companyExtensions = []),
    });

    this.svc.getTokenExtensionPlans().subscribe({
      next: (ext) =>
        (this.tokenExtensions = (ext || []).filter((e) => e.is_active !== false)),
      error: () => (this.tokenExtensions = []),
    });
  }

  loadHistory(): void {
    this.loadingHistory = true;
    this.svc.getHistory().subscribe({
      next: (res) => {
        this.history = res || [];
        this.loadingHistory = false;
      },
      error: () => {
        this.loadingHistory = false;
      },
    });
  }

  loadOrders(): void {
    this.loadingOrders = true;
    this.svc.getOrders().subscribe({
      next: (res) => {
        this.orders = res || [];
        this.loadingOrders = false;
      },
      error: () => {
        this.loadingOrders = false;
      },
    });
  }

  loadAutoRenew(): void {
    this.loadingAutoRenew = true;
    this.svc.getAutoRenew().subscribe({
      next: (res) => {
        this.autoRenew = res;
        this.loadingAutoRenew = false;
      },
      error: () => {
        this.loadingAutoRenew = false;
      },
    });
  }

  // ── Purchase flows ────────────────────────────────────────────────────────

  pickPlan(planId: number, type: 'subscription' | 'doc' | 'company' | 'token'): void {
    // إذا كنا في وضع اختيار خطة للتجديد التلقائي، لا نفتح مودال الشراء
    if (type === 'subscription' && this.autoRenewSelectionMode) {
      this.setAutoRenewPlan(planId);
      return;
    }

    this.selectedPlanType = type;
    this.selectedPlanId = planId;
    this.autoRenewOnPurchase = type === 'subscription';
    this.saveCardOnSubscriptionPurchase = type === 'subscription';
    this.saveCardOnAddonPurchase = false;
    this.loadSavedCardStatus();
  }

  /** When auto-renew is on without an existing saved card, saving the card is required */
  onSubscriptionAutoRenewChange(checked: boolean): void {
    if (checked && this.hasActiveSavedCard !== true) {
      this.saveCardOnSubscriptionPurchase = true;
    }
  }

  /** يبدأ وضع اختيار خطة للتجديد التلقائي ثم يفتح تبويب الخطط */
  startAutoRenewSelection(): void {
    this.autoRenewSelectionMode = true;
    this.activeTab = 'plans';
  }

  confirmPurchase(): void {
    if (!this.selectedPlanId || !this.merchantId) {
      return;
    }
    this.purchasing = true;
    this.spinner.show();

    let request$;
    const baseBody = {
      plan_id: this.selectedPlanId,
      merchant_id: this.merchantId,
      language: this.lang,
    } as const;

    if (this.selectedPlanType === 'subscription') {
      const saveCardForThisPurchase =
        this.hasActiveSavedCard !== true &&
        (this.autoRenewOnPurchase || this.saveCardOnSubscriptionPurchase);
      request$ = this.svc.purchaseSubscription({
        ...baseBody,
        save_card: saveCardForThisPurchase,
        auto_renew: this.autoRenewOnPurchase,
      });
    } else if (this.selectedPlanType === 'doc') {
      request$ = this.svc.purchaseDocExtension({
        ...baseBody,
        save_card:
          this.hasActiveSavedCard !== true && this.saveCardOnAddonPurchase,
      });
    } else if (this.selectedPlanType === 'company') {
      request$ = this.svc.purchaseCompanyExtension({
        ...baseBody,
        save_card:
          this.hasActiveSavedCard !== true && this.saveCardOnAddonPurchase,
      });
    } else {
      request$ = this.svc.purchaseTokenExtension({
        ...baseBody,
        save_card: false,
      });
    }

    (request$ as any).subscribe({
      next: (res: InvoicePurchaseResult) => {
        this.purchasing = false;
        this.spinner.hide();
        this.handlePurchaseResult(res);
      },
      error: (err: any) => {
        this.purchasing = false;
        this.spinner.hide();
        const msg =
          this.apiErrorMessage(err) || this.t('MerchantSubscriptions.purchaseError');
        this.closePurchaseModal();
        Swal.fire({
          icon: 'error',
          title: msg,
          confirmButtonText: this.lang === 'ar' ? 'حسناً' : this.lang === 'he' ? 'אישור' : 'OK',
        });
      },
    });
  }

  private handlePurchaseResult(res: InvoicePurchaseResult): void {
    if (res.type === 'payment_link') {
      window.open(res.payment_url, '_blank');
      const msg =
        (this.lang === 'ar' && res.message_ar) ||
        (this.lang === 'he' && res.message_he) ||
        res.message_en ||
        this.t('MerchantSubscriptions.paymentRedirect');
      this.toastr.info(msg, this.t('common.info'));
      this.loadOrders();
      this.closePurchaseModal();
    } else if (res.type === 'direct_charge') {
      if (res.success) {
        const msg =
          (this.lang === 'ar' && res.message_ar) ||
          (this.lang === 'he' && res.message_he) ||
          res.message_en ||
          this.t('MerchantSubscriptions.directChargeSuccess', { last4: res.card_last4 || '****' });
        this.toastr.success(msg, this.t('common.success'));
        this.refreshAll();
        this.closePurchaseModal();
      } else {
        const msg =
          (this.lang === 'ar' && res.message_ar) ||
          (this.lang === 'he' && res.message_he) ||
          res.message_en ||
          this.t('MerchantSubscriptions.purchaseError');
        this.toastr.error(msg, this.t('common.error'));
      }
    }
  }

  // ── Auto-renew ────────────────────────────────────────────────────────────

  enableAutoRenew(planId: number, options?: { afterPlanSelection?: boolean }): void {
    this.spinner.show();
    this.svc.enableAutoRenew(planId).subscribe({
      next: () => {
        this.spinner.hide();
        this.toastr.success(this.t('MerchantSubscriptions.autoRenewEnabled'), this.t('common.success'));
        this.loadAutoRenew();
        if (options?.afterPlanSelection) {
          this.navigateAfterAutoRenewPlanSelection();
        }
      },
      error: (err) => {
        this.spinner.hide();
        const msg =
          this.apiErrorMessage(err) || this.t('MerchantSubscriptions.autoRenewError');
        this.toastr.error(msg, this.t('common.error'));
      },
    });
  }

  /**
   * After choosing an auto-renew plan: if merchant has a saved card → Overview tab;
   * otherwise → add card page (PayPlus registration).
   */
  private navigateAfterAutoRenewPlanSelection(): void {
    if (!this.merchantId) {
      this.activeTab = 'overview';
      return;
    }
    this.merchantPayment.getSavedCardStatus(this.merchantId).subscribe({
      next: (res) => {
        this.hasActiveSavedCard = !!res?.has_active_saved_card;
        if (this.hasActiveSavedCard) {
          this.activeTab = 'overview';
          this.refreshAll();
          this.toastr.info(
            this.t('MerchantSubscriptions.autoRenewNavToOverview'),
            this.t('common.info'),
          );
        } else {
          this.toastr.info(
            this.t('MerchantSubscriptions.autoRenewNavToPayment'),
            this.t('common.info'),
          );
          this.router.navigate(['/dashboard/merchant-payment']);
        }
      },
      error: () => {
        this.hasActiveSavedCard = false;
        this.toastr.info(
          this.t('MerchantSubscriptions.autoRenewNavToPayment'),
          this.t('common.info'),
        );
        this.router.navigate(['/dashboard/merchant-payment']);
      },
    });
  }

  /** يختار خطة معينة لتكون خطة التجديد التلقائي ويرسلها للـ API */
  private setAutoRenewPlan(planId: number): void {
    this.autoRenewSelectionMode = false;
    this.enableAutoRenew(planId, { afterPlanSelection: true });
  }

  cancelAutoRenew(): void {
    if (!this.autoRenew?.auto_renew) {
      return;
    }
    const rtl = this.lang === 'ar' || this.lang === 'he';
    Swal.fire({
      icon: 'warning',
      title: this.t('MerchantSubscriptions.confirmCancelAutoRenewTitle'),
      html: `<p class="swal-cancel-autorenew__body">${this.t('MerchantSubscriptions.confirmCancelAutoRenew')}</p>`,
      showCancelButton: true,
      focusCancel: true,
      confirmButtonText: this.t('MerchantSubscriptions.confirmCancelAutoRenewConfirm'),
      cancelButtonText: this.t('MerchantSubscriptions.confirmCancelAutoRenewKeep'),
      confirmButtonColor: '#c62828',
      cancelButtonColor: '#5c6bc0',
      buttonsStyling: true,
      width: 'min(92vw, 22rem)',
      customClass: {
        ...(rtl ? { container: 'swal2-rtl' } : {}),
        popup: 'swal-cancel-autorenew',
        title: 'swal-cancel-autorenew__title',
        confirmButton: 'swal-cancel-autorenew__btn-confirm',
        cancelButton: 'swal-cancel-autorenew__btn-cancel',
        actions: 'swal-cancel-autorenew__actions',
      },
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }
      this.spinner.show();
      this.svc.cancelAutoRenew().subscribe({
        next: (res) => {
          this.spinner.hide();
          const msg =
            (this.lang === 'ar' && res.message_ar) ||
            (this.lang === 'he' && res.message_he) ||
            res.message_en ||
            this.t('MerchantSubscriptions.cancelSuccess');
          this.toastr.success(msg, this.t('common.success'));
          this.loadAutoRenew();
        },
        error: (err) => {
          this.spinner.hide();
          const msg =
            this.apiErrorMessage(err) || this.t('MerchantSubscriptions.cancelError');
          this.toastr.error(msg, this.t('common.error'));
        },
      });
    });
  }

  cancelPendingOrder(order: InvoiceSubscriptionOrder): void {
    if (!this.canCancelOrder(order) || this.cancellingOrderId) {
      return;
    }

    Swal.fire({
      icon: 'warning',
      title: this.t('MerchantSubscriptions.cancelPendingOrderConfirm'),
      showCancelButton: true,
      confirmButtonText: this.t('MerchantSubscriptions.cancelPendingOrder'),
      cancelButtonText: this.t('MerchantSubscriptions.keepPendingOrder'),
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }

      this.cancellingOrderId = order.id;
      this.svc.cancelOrder(order.id).subscribe({
        next: (res: InvoiceCancelOrderResponse) => {
          this.cancellingOrderId = null;
          const msg =
            this.localizedApiMessage(res) || this.t('MerchantSubscriptions.pendingOrderCancelled');
          this.toastr.success(msg, this.t('common.success'));
          this.loadOrders();
        },
        error: (err) => {
          this.cancellingOrderId = null;
          const msg =
            this.apiErrorMessage(err) || this.t('MerchantSubscriptions.pendingOrderCancelError');
          Swal.fire({
            icon: 'error',
            title: msg,
            confirmButtonText: this.resolveLang() === 'ar' ? 'حسناً' : this.resolveLang() === 'he' ? 'אישור' : 'OK',
          });
        },
      });
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  planName(p: { name_ar?: string; name_en?: string; name_he?: string }): string {
    const ar = (p.name_ar || '').trim();
    const en = (p.name_en || '').trim();
    const he = (p.name_he || '').trim();
    if (this.lang === 'ar') return ar || en || he;
    if (this.lang === 'he') return he || en || ar;
    return en || ar || he;
  }

  planDescription(p: InvoiceSubscriptionPlan): string {
    if (this.lang === 'ar') return (p.description_ar || '').trim();
    if (this.lang === 'he') return (p.description_he || p.description_ar || '').trim();
    return (p.description_en || p.description_ar || '').trim();
  }

  subscriptionStatusBadge(s: InvoiceSubscriptionStatus | null): string {
    if (!s || !s.subscription) return 'badge-secondary';
    if (s.is_active) return 'badge-success';
    if (s.is_expired) return 'badge-danger';
    return 'badge-secondary';
  }

  currentPlanName(): string {
    if (!this.status?.subscription) {
      return '';
    }
    const sub = this.status.subscription;
    const embedded = sub.plan;
    if (embedded && (embedded.name_ar || embedded.name_en || embedded.name_he)) {
      const n = this.planName(embedded);
      if (n) return n;
    }
    const fromCatalog = this.plans.find((p) => p.id === sub.plan_id);
    if (fromCatalog) {
      return this.planName(fromCatalog);
    }
    return '';
  }

  /** CSS modifiers for `.order-status` — avoids Bootstrap `.badge-warning` washing out text */
  orderStatusClasses(status: string | undefined): string[] {
    const s = (status || '').toLowerCase();
    const mod =
      s === 'completed'
        ? 'order-status--completed'
        : s === 'failed'
          ? 'order-status--failed'
          : s === 'pending'
            ? 'order-status--pending'
            : s === 'cancelled' || s === 'canceled'
              ? 'order-status--cancelled'
              : s === 'active'
                ? 'order-status--active'
                : s === 'expired'
                  ? 'order-status--expired'
                  : 'order-status--neutral';
    return ['order-status', mod];
  }

  orderStatusLabel(status: string | undefined): string {
    const s = (status || '').toLowerCase();
    const key = `MerchantSubscriptions.orderStatus.${s}`;
    const tr = this.translate.instant(key);
    return tr !== key ? tr : (status || '—');
  }

  orderTypeLabel(type: string | undefined): string {
    const raw = (type || '').trim();
    const t = raw.toLowerCase().replace(/-/g, '_');
    const key = `MerchantSubscriptions.orderTypes.${t}`;
    const tr = this.translate.instant(key);
    return tr !== key ? tr : raw || '—';
  }

  orderTypeClasses(type: string | undefined): string[] {
    const t = (type || '').toLowerCase().replace(/-/g, '_');
    let mod = 'order-type--other';
    if (t === 'subscription') mod = 'order-type--subscription';
    else if (t === 'token_extension') mod = 'order-type--token';
    else if (t === 'doc_extension') mod = 'order-type--doc';
    else if (t === 'company_extension') mod = 'order-type--company';
    return ['order-type-chip', mod];
  }

  canCancelOrder(order: InvoiceSubscriptionOrder): boolean {
    return (order.status || '').toLowerCase() === 'pending';
  }

  /** Coerce plan price — API may return string; JS `"199" > "99"` is false (lexicographic). */
  private planPriceNum(plan: InvoiceSubscriptionPlan): number {
    const n = Number(plan.price);
    return Number.isFinite(n) ? n : 0;
  }

  /**
   * علاقة الخطة بالاشتراك الحالي:
   * 'current'  — هذه هي الخطة المشترك فيها حالياً
   * 'upgrade'  — خطة أعلى (سعر رقمي أعلى)
   * 'downgrade'— خطة أقل
   * 'none'     — لا يوجد اشتراك نشط
   */
  planRelation(plan: InvoiceSubscriptionPlan): 'current' | 'upgrade' | 'downgrade' | 'none' {
    if (!this.status?.subscription || !this.status.is_active) {
      return 'none';
    }
    const currentPlanId = this.status.subscription.plan_id;
    if (plan.id === currentPlanId) {
      return 'current';
    }
    const currentPlan = this.plans.find((p) => p.id === currentPlanId);
    if (!currentPlan) {
      return 'none';
    }
    const cur = this.planPriceNum(currentPlan);
    const pr = this.planPriceNum(plan);
    if (pr > cur) return 'upgrade';
    if (pr < cur) return 'downgrade';
    return 'downgrade';
  }

  /**
   * أرخص خطة أغلى من الحالية — «الترقية التالية» في الواجهة.
   */
  isNextUpgradePlan(plan: InvoiceSubscriptionPlan): boolean {
    if (this.planRelation(plan) !== 'upgrade') {
      return false;
    }
    const currentPlan = this.plans.find((p) => p.id === this.status!.subscription!.plan_id);
    if (!currentPlan) {
      return false;
    }
    const threshold = this.planPriceNum(currentPlan);
    const higherPrices = this.plans
      .filter((p) => this.planPriceNum(p) > threshold)
      .map((p) => this.planPriceNum(p));
    if (higherPrices.length === 0) {
      return false;
    }
    const minHigher = Math.min(...higherPrices);
    return this.planPriceNum(plan) === minHigher;
  }

  /** هل الرابط منتهي الصلاحية؟ — نعتمد على الحقل من الباك‑إند أو نحسبه محلياً */
  isOrderLinkExpired(order: InvoiceSubscriptionOrder): boolean {
    if (order.is_link_expired) {
      return true;
    }
    if (order.link_expiry_datetime) {
      return new Date(order.link_expiry_datetime) < new Date();
    }
    return false;
  }

  /** هل يمكن فتح رابط الدفع لهذا الطلب؟ */
  canOpenOrderLink(order: InvoiceSubscriptionOrder): boolean {
    return (
      (order.status || '').toLowerCase() === 'pending' &&
      !!order.payment_url &&
      !this.isOrderLinkExpired(order)
    );
  }

  openOrderPaymentLink(order: InvoiceSubscriptionOrder): void {
    if (!this.canOpenOrderLink(order)) {
      return;
    }
    window.open(order.payment_url!, '_blank', 'noopener,noreferrer');
  }

  /** نص توقيت انتهاء الصلاحية */
  orderLinkExpiryLabel(order: InvoiceSubscriptionOrder): string {
    if (!order.link_expiry_datetime) {
      return '';
    }
    const lang = this.resolveLang();
    const formatted = this.formatDateTime(order.link_expiry_datetime);
    if (this.isOrderLinkExpired(order)) {
      if (lang === 'ar') return `انتهى الرابط: ${formatted}`;
      if (lang === 'he') return `הקישור פג: ${formatted}`;
      return `Link expired: ${formatted}`;
    }
    if (lang === 'ar') return `صالح حتى: ${formatted}`;
    if (lang === 'he') return `בתוקף עד: ${formatted}`;
    return `Valid until: ${formatted}`;
  }

  formatDateTime(dateStr: string): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  }

  txStatusBadge(status: string): string {
    if (!status) return 'bg-secondary';
    switch (status.toUpperCase()) {
      case 'SUCCESS':
        return 'bg-success';
      case 'FAILED':
        return 'bg-danger';
      case 'PENDING':
        return 'bg-warning text-dark';
      default:
        return 'bg-secondary';
    }
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }

  get completedOrdersCount(): number {
    return this.orders.filter((o) => o.status === 'completed').length;
  }

  /** Show analysis-token quota bar in overview when API sends a limit. */
  showAnalysisTokenUsage(): boolean {
    const lim = this.status?.subscription?.tokens_limit;
    return lim != null && lim > 0;
  }

  analysisTokensUsed(): number {
    return this.status?.subscription?.tokens_used ?? 0;
  }

  analysisTokensLimit(): number {
    return this.status?.subscription?.tokens_limit ?? 0;
  }

  analysisTokensRemaining(): number {
    if (this.status?.tokens_remaining != null) {
      return this.status.tokens_remaining;
    }
    return Math.max(0, this.analysisTokensLimit() - this.analysisTokensUsed());
  }

  analysisTokensUsagePercent(): number {
    const lim = this.analysisTokensLimit();
    if (!lim) return 0;
    return Math.min(100, Math.round((this.analysisTokensUsed() / lim) * 100));
  }

  /** 0–100 for progress widths */
  clampPct(value: number | null | undefined): number {
    if (value == null || !Number.isFinite(value)) return 0;
    return Math.max(0, Math.min(100, Math.round(value)));
  }

  /** % of subscription window elapsed (start → end) */
  subscriptionPeriodElapsedPercent(): number | null {
    const s = this.status?.subscription;
    if (!s?.start_date || !s?.end_date) return null;
    const start = new Date(s.start_date).getTime();
    const end = new Date(s.end_date).getTime();
    const now = Date.now();
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
    if (now <= start) return 0;
    if (now >= end) return 100;
    return ((now - start) / (end - start)) * 100;
  }

  overviewDocumentsUsedPct(): number {
    const s = this.status?.subscription;
    if (!s?.documents_limit) return 0;
    return (s.documents_used / s.documents_limit) * 100;
  }

  overviewDocumentsRemainingPct(): number {
    const s = this.status?.subscription;
    if (!s?.documents_limit) return 0;
    const rem =
      this.status?.documents_remaining ?? Math.max(0, s.documents_limit - s.documents_used);
    return (rem / s.documents_limit) * 100;
  }

  overviewCompaniesUsedPct(): number {
    const s = this.status?.subscription;
    if (!s?.companies_limit) return 0;
    return (s.companies_used / s.companies_limit) * 100;
  }

  overviewCompaniesRemainingPct(): number {
    const s = this.status?.subscription;
    if (!s?.companies_limit) return 0;
    const rem =
      this.status?.companies_remaining ?? Math.max(0, s.companies_limit - s.companies_used);
    return (rem / s.companies_limit) * 100;
  }

  analysisTokensRemainingPct(): number {
    if (!this.showAnalysisTokenUsage()) return 0;
    const lim = this.analysisTokensLimit();
    if (!lim) return 0;
    return (this.analysisTokensRemaining() / lim) * 100;
  }

  pendingPayableOrdersCount(): number {
    return this.orders.filter((o) => this.canOpenOrderLink(o)).length;
  }

  /** Returns the index of the currently selected plan within `this.plans` */
  selectedPlanIndex(): number {
    return this.plans.findIndex((p) => p.id === this.selectedPlanId);
  }

  /** Returns the index of the selected extension (doc or company) */
  selectedExtIndex(type: 'doc' | 'company' | 'token'): number {
    if (type === 'doc') {
      return this.docExtensions.findIndex((e) => e.id === this.selectedPlanId);
    }
    if (type === 'company') {
      return this.companyExtensions.findIndex((e) => e.id === this.selectedPlanId);
    }
    return this.tokenExtensions.findIndex((e) => e.id === this.selectedPlanId);
  }

  tokenExtensionLabel(e: InvoiceTokenExtensionPlan): string {
    return this.planName(e);
  }

  addonDisplayName(e: { name_ar: string; name_en: string }): string {
    if (this.lang === 'ar') return e.name_ar;
    if (this.lang === 'he') return e.name_ar || e.name_en;
    return e.name_en;
  }

  private closePurchaseModal(): void {
    this.selectedPlanId = null;
    this.autoRenewOnPurchase = false;
    this.saveCardOnAddonPurchase = false;
  }

  /**
   * يقرأ رسالة الخطأ من HttpErrorResponse (400/403/…)
   * يدعم: جسم JSON كائن أو نص JSON، و messageEn/messageAr/messageHe و message_en…
   */
  private apiErrorMessage(err: any): string {
    let e = err?.error;
    if (e == null || e === '') {
      return '';
    }
    if (typeof e === 'string') {
      const trimmed = e.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          e = JSON.parse(trimmed);
        } catch {
          return trimmed;
        }
      } else {
        return trimmed;
      }
    }
    if (typeof e !== 'object') {
      return '';
    }
    if (e.error && typeof e.error === 'object') {
      e = e.error;
    }
    const ar = e.messageAr ?? e.message_ar;
    const en = e.messageEn ?? e.message_en;
    const he = e.messageHe ?? e.message_he;
    const lang = this.resolveLang();
    if (lang === 'ar') {
      return String(ar || en || he || e.message || e.detail || '').trim();
    }
    if (lang === 'he') {
      return String(he || en || ar || e.message || e.detail || '').trim();
    }
    return String(en || ar || he || e.message || e.detail || '').trim();
  }

  private localizedApiMessage(payload: any): string {
    if (!payload || typeof payload !== 'object') {
      return '';
    }
    const ar = payload.messageAr ?? payload.message_ar;
    const en = payload.messageEn ?? payload.message_en;
    const he = payload.messageHe ?? payload.message_he;
    const lang = this.resolveLang();
    if (lang === 'ar') {
      return String(ar || en || he || payload.message || payload.detail || '').trim();
    }
    if (lang === 'he') {
      return String(he || en || ar || payload.message || payload.detail || '').trim();
    }
    return String(en || ar || he || payload.message || payload.detail || '').trim();
  }

  /** لغة الواجهة الفعلية (قد تختلف عن this.lang إن تغيّرت بدون إعادة تحميل) */
  private resolveLang(): Lang {
    const s = (localStorage.getItem('curentLang') || this.lang || 'ar') as string;
    if (s === 'ar' || s === 'he' || s === 'en') {
      return s;
    }
    return 'ar';
  }

  private t(key: string, params?: any): string {
    return this.translate.instant(key, params);
  }
}

